import { useEffect, useRef } from 'react'
import { createSequenceMatcher, isEditableTarget, type HotkeyBinding } from '../logic/hotkeys.ts'

export interface Hotkey extends HotkeyBinding {
  handler: (e: KeyboardEvent) => void
  /** Shown in the shortcuts sheet. */
  description?: string
  group?: string
  enabled?: boolean
}

/**
 * Registers global shortcuts (chords, `g h` sequences, single keys). Ignores keys typed into
 * fields unless `allowInInput`, and anything while a modal dialog is open other than its own keys.
 */
export function useHotkeys(bindings: readonly Hotkey[]) {
  const ref = useRef(bindings)
  ref.current = bindings
  useEffect(() => {
    let matcher = createSequenceMatcher(ref.current.filter((b) => b.enabled !== false))
    let snapshot = ref.current
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return
      if (snapshot !== ref.current) {
        snapshot = ref.current
        matcher = createSequenceMatcher(snapshot.filter((b) => b.enabled !== false))
      }
      const modalOpen = !!document.querySelector('[role="dialog"][data-state="open"]')
      const editable = isEditableTarget(e.target)
      const hit = matcher.feed(e, performance.now(), editable)
      if (!hit) return
      if (modalOpen && !hit.allowInInput) return
      e.preventDefault()
      hit.handler(e)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}
